import React, { useState } from 'react';
import { Button, Box, CircularProgress, Typography, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { queueAccountForDeletion, reauthenticateUser, reauthenticateWithGoogle } from 'apis/authApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { useAuth } from 'contexts/AuthContext';
import { GoogleIcon } from 'icons/brandIcons'; // Ensure this import works

const DeleteUser = () => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [reauthDialog, setReauthDialog] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [accountQueued, setAccountQueued] = useState(false);

  const navigate = useNavigate();
  const { logout, authState } = useAuth();

  const handleCredentialChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleReauthenticateWithPassword = async () => {
    setLoading(true);
    try {
      // Pre-fill email if available
      const email = credentials.email || authState?.user?.email;

      if (!email || !credentials.password) {
        throw new Error('Email and password are required');
      }

      await reauthenticateUser(email, credentials.password);
      setReauthDialog(false);

      // Proceed with deletion after successful reauthentication
      console.log("Reauthentication successful, proceeding with deletion");
      handleQueueForDeletion(true); // Pass true to indicate this is after reauthentication
    } catch (error) {
      console.error('Reauthentication error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to authenticate',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReauthenticateWithGoogle = async () => {
    setLoading(true);
    try {
      const result = await reauthenticateWithGoogle();
      if (result.success) {
        setReauthDialog(false);

        // Proceed with deletion after successful reauthentication
        console.log("Google reauthentication successful, proceeding with deletion");
        handleQueueForDeletion(true);
      } else {
        throw new Error('Google authentication failed');
      }
    } catch (error) {
      console.error('Google reauthentication error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to authenticate with Google',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQueueForDeletion = async (isAfterReauth = false) => {
    setLoading(true);
    try {
      console.log("Attempting to queue account for deletion, isAfterReauth:", isAfterReauth);
      const result = await queueAccountForDeletion();

      console.log("Account queued for deletion successfully:", result);
      setSnackbar({
        open: true,
        message: result.message || 'Your account deletion has been initiated. This process should complete shortly.',
        severity: 'success',
      });

      setAccountQueued(true);

      // Logout after a delay
      setTimeout(async () => {
        await logout();
        navigate('/');
      }, 4000);
    } catch (error) {
      console.error('Error queueing account for deletion:', error);

      // Check if reauthentication is required
      if ((error.requiresReauth || error.code === 'auth/requires-recent-login') && !isAfterReauth) {
        console.log("Reauthentication required, showing dialog");
        // Show reauthentication dialog
        setReauthDialog(true);
      } else {
        setSnackbar({
          open: true,
          message: error.message || 'Failed to delete account',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <Typography variant="h5" gutterBottom>
        Delete Account
      </Typography>
      <Typography variant="body1" gutterBottom>
        Click the button below to permanently delete your account and all associated data.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Note: This action cannot be undone. All your data will be permanently removed.
      </Typography>

      {accountQueued ? (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1">
            Your account is being deleted. You will be logged out automatically.
          </Typography>
        </Box>
      ) : (
        <Button
          variant="contained"
          color="error"
          onClick={() => handleQueueForDeletion(false)}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Delete User'}
        </Button>
      )}

      {/* Reauthentication Dialog with BOTH options */}
      <Dialog open={reauthDialog} onClose={() => !loading && setReauthDialog(false)}>
        <DialogTitle>Verification Required</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            For security reasons, please verify your identity to confirm account deletion.
          </Typography>

          {/* Google auth option (prominent for Google users) */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleReauthenticateWithGoogle}
            disabled={loading}
            sx={{ mt: 1, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify with Google'}
          </Button>

          <Divider sx={{ my: 2 }}>OR</Divider>

          {/* Password option as a fallback */}
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            If you've set up password authentication, you can also verify with:
          </Typography>

          <TextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={credentials.email || authState?.user?.email || ''}
            onChange={handleCredentialChange}
            disabled={loading}
          />
          <TextField
            margin="dense"
            name="password"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={credentials.password}
            onChange={handleCredentialChange}
            disabled={loading}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleReauthenticateWithPassword}
            disabled={loading || !credentials.password}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify with Password'}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReauthDialog(false)} disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default DeleteUser;